<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-account-group-outline me-2"></i>{{ $t('page.hrh', 'HRH — Organogram & Duty Roster') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ orgStats.total_staff || 0 }}</div><div class="hms-stat-lbl">Active Staff</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ orgStats.medical || 0 }}</div><div class="hms-stat-lbl">Medical</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ orgStats.nursing || 0 }}</div><div class="hms-stat-lbl">Nursing</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ (orgStats.pharmacy || 0) + (orgStats.lab || 0) + (orgStats.allied || 0) }}</div><div class="hms-stat-lbl">Allied / Diagnostic</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ rosterStats.active_today || 0 }}</div><div class="hms-stat-lbl">On shift today</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='org' }" @click="tab='org'"><i class="mdi mdi-sitemap me-1"></i>Organogram</button>
        <button class="hms-tab" :class="{ active: tab==='roster' }" @click="tab='roster'; loadRoster()"><i class="mdi mdi-calendar-week me-1"></i>Duty Roster</button>
      </div>

      <!-- ============ ORGANOGRAM ============ -->
      <div v-if="tab==='org'">
        <div class="d-flex justify-content-between mb-2">
          <select v-model="orgScope" class="hms-input" @change="loadOrg" style="max-width:200px">
            <option value="branch">This facility</option>
            <option value="county">All county facilities</option>
          </select>
        </div>
        <div class="hms-org-grid">
          <div v-for="d in departments" :key="d.department_id" class="hms-org-dept">
            <div class="hms-org-head">
              <div class="hms-org-dept-name">
                <i class="mdi mdi-office-building"></i>
                {{ d.department_name }}
                <span class="hms-muted-sm" v-if="orgScope === 'county'"> · {{ d.branch_name }}</span>
              </div>
              <span class="hms-pill hms-pill-info">{{ d.staff_count || 0 }} staff</span>
            </div>
            <div v-if="d.head_name" class="hms-org-headof">
              <div class="hms-org-headavatar"><i class="mdi mdi-account-tie"></i></div>
              <div>
                <div class="hms-org-headname">{{ d.head_name }}</div>
                <div class="hms-muted-sm">Head of Department · {{ d.head_cadre_name || '—' }}</div>
              </div>
            </div>
            <div v-else class="hms-org-headof hms-org-no-head">
              <i class="mdi mdi-account-question"></i> Department head not assigned
            </div>
            <div class="hms-org-staff">
              <div v-for="s in staffByDept(d)" :key="s.staff_id" class="hms-org-card" :class="'cat-' + s.cadre_category">
                <div class="hms-org-name">{{ s.first_name }} {{ s.last_name }}</div>
                <div class="hms-muted-sm">{{ s.cadre_code || '—' }} · {{ s.specialization || s.cadre_name || '' }}</div>
              </div>
              <div v-if="!staffByDept(d).length" class="hms-muted-sm hms-pad">No staff assigned</div>
            </div>
          </div>
        </div>

        <div class="hms-card mt-3">
          <div class="hms-card-head">Staff by cadre category</div>
          <div class="hms-card-body">
            <div v-for="cat in cadreCategories" :key="cat.code" class="hms-cat-row-2">
              <div class="hms-cat-label">
                <i :class="cat.icon"></i>
                <strong>{{ cat.label }}</strong>
              </div>
              <div class="hms-cat-bar"><div class="hms-cat-fill" :style="{ width: catBarPct(cat) + '%' }"></div></div>
              <div class="hms-cat-num">{{ orgStats[cat.statKey] || 0 }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ DUTY ROSTER ============ -->
      <div v-if="tab==='roster'">
        <div class="d-flex justify-content-between mb-2 flex-wrap gap-2">
          <div class="d-flex gap-2 align-items-center">
            <button class="hms-btn hms-btn-secondary" @click="shiftWeek(-1)"><i class="mdi mdi-chevron-left"></i></button>
            <strong>{{ formatDate(weekStart) }} — {{ formatDate(weekEnd) }}</strong>
            <button class="hms-btn hms-btn-secondary" @click="shiftWeek(1)"><i class="mdi mdi-chevron-right"></i></button>
            <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="goToThisWeek">This week</button>
          </div>
          <div class="d-flex gap-2">
            <select v-model="rosterDeptFilter" class="hms-input" @change="loadRoster" style="max-width:240px">
              <option value="">All departments</option>
              <option v-for="d in deptsList" :key="d.department_id" :value="d.department_id">{{ d.department_name }}</option>
            </select>
            <button class="hms-btn hms-btn-success" @click="openShiftModal()"><i class="mdi mdi-plus"></i> Schedule Shift</button>
          </div>
        </div>

        <!-- Week grid -->
        <div class="hms-card">
          <div class="hms-card-body p-0" style="overflow-x:auto">
            <table class="hms-roster-grid">
              <thead>
                <tr>
                  <th style="min-width:160px">Staff</th>
                  <th v-for="(day, i) in weekDays" :key="i" :class="day.isToday ? 'today' : ''">
                    <div>{{ day.label }}</div>
                    <div class="hms-muted-sm">{{ day.date }}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="staff in rosterStaff" :key="staff.staff_id">
                  <td class="hms-roster-staff">
                    <strong>{{ staff.first_name }} {{ staff.last_name }}</strong>
                    <div class="hms-muted-sm">{{ staff.cadre_code || '—' }} · {{ staff.department || '' }}</div>
                  </td>
                  <td v-for="(day, i) in weekDays" :key="i" class="hms-roster-cell" :class="day.isToday ? 'today-cell' : ''">
                    <div v-for="sh in shiftsFor(staff.staff_id, day.date)" :key="sh.shift_id" class="hms-shift-chip" :class="'shift-' + sh.shift_type" @click="editShift(sh)">
                      <div class="hms-shift-type">{{ formatShiftType(sh.shift_type) }}</div>
                      <div v-if="sh.start_time" class="hms-shift-time">{{ sh.start_time.substring(0,5) }}-{{ sh.end_time ? sh.end_time.substring(0,5) : '' }}</div>
                      <div v-if="sh.ward_name" class="hms-shift-ward">{{ sh.ward_name }}</div>
                      <div v-else-if="sh.department_name" class="hms-shift-ward">{{ sh.department_name }}</div>
                    </div>
                    <button class="hms-add-shift-btn" @click="quickShift(staff, day.date)" :title="'Add shift on ' + day.date">+</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex gap-3 mt-2 flex-wrap hms-muted-sm">
          <span><span class="hms-shift-chip shift-morning hms-legend-chip"></span> Morning</span>
          <span><span class="hms-shift-chip shift-afternoon hms-legend-chip"></span> Afternoon</span>
          <span><span class="hms-shift-chip shift-night hms-legend-chip"></span> Night</span>
          <span><span class="hms-shift-chip shift-on_call hms-legend-chip"></span> On-call</span>
        </div>
      </div>
    </div>

    <!-- ====== SHIFT MODAL ====== -->
    <div v-if="shiftModal" class="hms-modal-bg" @click.self="shiftModal=false">
      <div class="hms-modal" style="width:540px">
        <div class="hms-modal-head"><h5>{{ shift.shift_id ? 'Edit Shift' : 'Schedule Shift' }}</h5><button class="hms-icon-btn" @click="shiftModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Staff *</label>
              <select v-model.number="shift.staff_id" class="hms-input">
                <option :value="null">— select —</option>
                <option v-for="s in allStaff" :key="s.staff_id" :value="s.staff_id">{{ s.first_name }} {{ s.last_name }} ({{ s.cadre_code || '—' }})</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Date *</label><input v-model="shift.shift_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Shift type</label>
              <select v-model="shift.shift_type" class="hms-input" @change="onShiftTypeChange">
                <option value="morning">Morning (07:00-15:00)</option>
                <option value="afternoon">Afternoon (15:00-23:00)</option>
                <option value="night">Night (23:00-07:00)</option>
                <option value="on_call">On-call (24h)</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Department</label>
              <select v-model.number="shift.department_id" class="hms-input">
                <option :value="null">—</option>
                <option v-for="d in deptsList" :key="d.department_id" :value="d.department_id">{{ d.department_name }}</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Start</label><input v-model="shift.start_time" type="time" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">End</label><input v-model="shift.end_time" type="time" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="shift.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button v-if="shift.shift_id" class="hms-btn hms-btn-link text-danger me-auto" @click="deleteShift"><i class="mdi mdi-trash-can"></i> Delete</button>
          <button class="hms-btn hms-btn-secondary" @click="shiftModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveShift">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

const SHIFT_TIMES = {
  morning:   { start: '07:00', end: '15:00' },
  afternoon: { start: '15:00', end: '23:00' },
  night:     { start: '23:00', end: '07:00' },
  on_call:   { start: '08:00', end: '08:00' },
  custom:    { start: '',      end: '' }
};

export default {
  name: "HrhManagement",
  components: { Navigation },
  data() {
    return {
      tab: 'org',
      saving: false,

      orgScope: 'branch',
      departments: [], allStaff: [], orgStats: {},

      weekStart: this.mondayOf(new Date()),
      shifts: [], rosterStats: {}, deptsList: [],
      rosterDeptFilter: '',

      shiftModal: false, shift: this.blankShift(),

      cadreCategories: [
        { code: 'medical',       label: 'Medical',      icon: 'mdi mdi-stethoscope',          statKey: 'medical' },
        { code: 'nursing',       label: 'Nursing',      icon: 'mdi mdi-account-heart',         statKey: 'nursing' },
        { code: 'pharmacy',      label: 'Pharmacy',     icon: 'mdi mdi-pill',                  statKey: 'pharmacy' },
        { code: 'laboratory',    label: 'Laboratory',   icon: 'mdi mdi-flask',                 statKey: 'lab' },
        { code: 'allied_health', label: 'Allied health',icon: 'mdi mdi-account-tie',           statKey: 'allied' },
        { code: 'admin',         label: 'Admin / Support',icon: 'mdi mdi-briefcase-account',  statKey: 'admin_support' }
      ]
    };
  },
  computed: {
    weekEnd() { return this.addDays(this.weekStart, 6); },
    weekDays() {
      const days = [];
      const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const today = new Date().toISOString().substring(0,10);
      for (let i = 0; i < 7; i++) {
        const d = this.addDays(this.weekStart, i);
        days.push({ date: d, label: labels[i], isToday: d === today });
      }
      return days;
    },
    rosterStaff() {
      // Unique staff from shifts; show staff who have at least one shift this week, sorted
      const map = {};
      this.shifts.forEach(s => {
        if (!map[s.staff_id]) {
          map[s.staff_id] = { staff_id: s.staff_id, first_name: s.staff_name ? s.staff_name.split(' ')[0] : '', last_name: s.staff_name ? s.staff_name.split(' ').slice(1).join(' ') : '', cadre_code: s.cadre_code, department: s.department_name };
        }
      });
      return Object.values(map).sort((a,b) => (a.last_name || '').localeCompare(b.last_name || ''));
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    blankShift() {
      return { shift_id: null, staff_id: null, ward_id: null, department_id: null, shift_type: 'morning', shift_date: new Date().toISOString().substring(0,10), start_time: '07:00', end_time: '15:00', status: 'scheduled', notes: '' };
    },
    mondayOf(d) {
      const date = new Date(d);
      const day = date.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      date.setDate(date.getDate() + diff);
      return date.toISOString().substring(0,10);
    },
    addDays(d, n) {
      const date = new Date(d);
      date.setDate(date.getDate() + n);
      return date.toISOString().substring(0,10);
    },
    formatDate(d) {
      const date = new Date(d);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },

    async loadAll() {
      await Promise.all([this.loadOrg(), this.loadStaffList(), this.loadDeptsList()]);
    },
    async loadOrg() {
      try {
        const r = await executeJson({ function: 'getHrhOrganogram', scope: this.orgScope }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.departments = d.Departments || [];
          this.allStaff    = d.Staff || [];
          this.orgStats    = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadStaffList() {
      // Re-use organogram staff for the shift modal selector if branch-scoped
      // Otherwise fetch branch-scoped list separately
      if (this.orgScope !== 'branch') {
        try {
          const r = await executeJson({ function: 'getStaffList' }, hms);
          if (r.data && r.data.success) this.allStaff = (r.data.data && r.data.data.Staff) || [];
        } catch (e) { console.error(e); }
      }
    },
    async loadDeptsList() {
      try {
        const r = await executeJson({ function: 'getDepartmentsList' }, hms);
        if (r.data && r.data.success) this.deptsList = (r.data.data && r.data.data.Departments) || [];
      } catch (e) { console.error(e); }
    },
    async loadRoster() {
      try {
        const r = await executeJson({ function: 'getDutyRoster', week_start: this.weekStart, department_id: this.rosterDeptFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.shifts = d.Shifts || [];
          this.rosterStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    shiftWeek(delta) {
      this.weekStart = this.addDays(this.weekStart, delta * 7);
      this.loadRoster();
    },
    goToThisWeek() {
      this.weekStart = this.mondayOf(new Date());
      this.loadRoster();
    },

    staffByDept(d) {
      return this.allStaff.filter(s =>
        s.branch_id == d.branch_id && (s.department || '').toLowerCase() === (d.department_name || '').toLowerCase()
      );
    },
    catBarPct(cat) {
      const total = parseInt(this.orgStats.total_staff || 0);
      if (total <= 0) return 0;
      return Math.round((parseInt(this.orgStats[cat.statKey] || 0) / total) * 100);
    },

    shiftsFor(staffId, date) {
      return this.shifts.filter(s => s.staff_id == staffId && s.shift_date === date);
    },
    formatShiftType(t) {
      return ({ morning: 'AM', afternoon: 'PM', night: 'NIGHT', on_call: 'ON-CALL', custom: 'CUSTOM' })[t] || t;
    },

    openShiftModal() {
      this.shift = this.blankShift();
      this.shift.shift_date = new Date().toISOString().substring(0,10);
      this.shiftModal = true;
    },
    quickShift(staff, date) {
      this.shift = this.blankShift();
      this.shift.staff_id = staff.staff_id;
      this.shift.shift_date = date;
      this.shiftModal = true;
    },
    editShift(sh) {
      this.shift = {
        shift_id: sh.shift_id, staff_id: sh.staff_id, ward_id: sh.ward_id, department_id: sh.department_id,
        shift_type: sh.shift_type, shift_date: sh.shift_date,
        start_time: sh.start_time ? sh.start_time.substring(0,5) : '',
        end_time:   sh.end_time   ? sh.end_time.substring(0,5)   : '',
        status: sh.status || 'scheduled',
        notes: sh.notes || ''
      };
      this.shiftModal = true;
    },
    onShiftTypeChange() {
      const t = SHIFT_TIMES[this.shift.shift_type];
      if (t) {
        this.shift.start_time = t.start;
        this.shift.end_time   = t.end;
      }
    },
    async saveShift() {
      if (!this.shift.staff_id || !this.shift.shift_date) { alert('Staff + date are required.'); return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveStaffShift', ...this.shift }, hms);
        if (r.data && r.data.success) { this.shiftModal = false; await this.loadRoster(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async deleteShift() {
      if (!confirm('Remove this shift?')) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'deleteStaffShift', shift_id: this.shift.shift_id }, hms);
        if (r.data && r.data.success) { this.shiftModal = false; await this.loadRoster(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

/* ===== ORGANOGRAM ===== */
.hms-org-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 12px; }
.hms-org-dept { background: #fff; border: 1px solid #e2e8f0; border-top: 3px solid #2b6cb0; border-radius: 8px; padding: 14px; }
.hms-org-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.hms-org-dept-name { font-weight: 600; color: #1a202c; font-size: 15px; display: flex; align-items: center; gap: 6px; }
.hms-org-dept-name .mdi { color: #2b6cb0; }
.hms-org-headof { display: flex; align-items: center; gap: 10px; padding: 10px; background: #ebf8ff; border-radius: 6px; margin-bottom: 10px; }
.hms-org-no-head { background: #fffaf0; color: #7b341e; }
.hms-org-headavatar { width: 38px; height: 38px; border-radius: 50%; background: #2b6cb0; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 18px; }
.hms-org-headname { font-weight: 600; color: #1a202c; }
.hms-org-staff { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.hms-org-card { padding: 6px 10px; border-radius: 6px; font-size: 12px; background: #f7fafc; border-left: 3px solid #cbd5e0; }
.hms-org-card.cat-medical       { border-left-color: #e53e3e; }
.hms-org-card.cat-nursing       { border-left-color: #2b6cb0; }
.hms-org-card.cat-pharmacy      { border-left-color: #16a34a; }
.hms-org-card.cat-laboratory    { border-left-color: #d69e2e; }
.hms-org-card.cat-allied_health { border-left-color: #7c3aed; }
.hms-org-card.cat-admin         { border-left-color: #64748b; }
.hms-org-card.cat-support       { border-left-color: #94a3b8; }
.hms-org-name { font-weight: 600; color: #1a202c; }

.hms-cat-row-2 { display: grid; grid-template-columns: 200px 1fr 60px; gap: 12px; align-items: center; padding: 6px 0; border-bottom: 1px solid #edf2f7; }
.hms-cat-label .mdi { color: #4a5568; margin-right: 6px; }
.hms-cat-bar  { background: #edf2f7; border-radius: 4px; height: 14px; overflow: hidden; }
.hms-cat-fill { background: linear-gradient(90deg, #4299e1, #2b6cb0); height: 100%; }
.hms-cat-num  { text-align: right; font-weight: 700; color: #1a202c; }

/* ===== ROSTER ===== */
.hms-roster-grid { width: 100%; border-collapse: collapse; font-size: 12px; background: #fff; }
.hms-roster-grid th { background: #f7fafc; padding: 8px 10px; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-roster-grid th.today { background: #ebf8ff; color: #2b6cb0; }
.hms-roster-grid td { padding: 6px; border: 1px solid #edf2f7; vertical-align: top; min-width: 110px; }
.hms-roster-staff { background: #f7fafc; min-width: 160px; }
.hms-roster-cell  { position: relative; min-height: 60px; }
.hms-roster-cell.today-cell { background: #fef5e7; }

.hms-shift-chip { display: block; padding: 4px 6px; margin-bottom: 4px; border-radius: 4px; cursor: pointer; font-size: 11px; line-height: 1.3; }
.hms-shift-chip.shift-morning   { background: #c6f6d5; color: #22543d; border-left: 3px solid #38a169; }
.hms-shift-chip.shift-afternoon { background: #feebc8; color: #7b341e; border-left: 3px solid #dd6b20; }
.hms-shift-chip.shift-night     { background: #e9d8fd; color: #44337a; border-left: 3px solid #6b46c1; }
.hms-shift-chip.shift-on_call   { background: #fed7d7; color: #742a2a; border-left: 3px solid #c53030; }
.hms-shift-chip.shift-custom    { background: #edf2f7; color: #4a5568; border-left: 3px solid #94a3b8; }
.hms-shift-type { font-weight: 700; }
.hms-shift-time { font-size: 10px; opacity: .85; }
.hms-shift-ward { font-size: 10px; opacity: .85; }

.hms-add-shift-btn { position: absolute; bottom: 4px; right: 4px; width: 22px; height: 22px; border-radius: 50%; border: 1px dashed #cbd5e0; background: #fff; color: #a0aec0; cursor: pointer; font-weight: 700; }
.hms-add-shift-btn:hover { background: #ebf8ff; border-color: #2b6cb0; color: #2b6cb0; }

.hms-legend-chip { display: inline-block; width: 14px; height: 14px; border-radius: 4px; margin-right: 4px; vertical-align: middle; padding: 0 !important; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-info { background: #bee3f8; color: #2a4365; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; align-items: center; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }
.text-danger { color: #c53030; }

.me-auto { margin-right: auto; }
.hms-pad { padding: 8px; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
</style>
